import { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { Selection, SelectionType } from "@dextall/panel-hooks-editor-2d";
import { isAlmostEqual, roundToDecimalSigns } from "@dextall/shared";
import Button from "@weave-design/button";
import { ProcessingPane } from "../../../../processing-pane/processingPane";
import { rootElement } from "../../../../shared";
import { createWindowAreaParameterInfo } from "../../../editor-inputs/info";
import { NumberPropertyEditor } from "../../../editor-inputs/numberPropertyEditor";
import { Panel } from "../../../panels/panel";
import { ParametersLabel } from "../parametersLabel";
import eventBus from "../../../eventBus/eventDispatcher";
import "./windowDesignParameters.css";

type Props = {
    panel: Panel;
    selection: Selection;
};

export const WindowDesignParameters = ({ panel, selection }: Props) => {
    const [windowArea, setWindowArea] = useState(0);
    const [originalWindowArea, setOriginalWindowArea] = useState(0);
    const [windowWeight, setWindowWeight] = useState(0);
    const [originalWindowWeight, setOriginalWindowWeight] = useState(0);
    const [isSaveInProgress, setIsSaveInProgress] = useState(false);

    useEffect(() => {
        const onComponentSelectionChanged = () => {
            if (selection.type !== SelectionType.Window) {
                throw new Error("WindowDesignParameters: Selection is not a window.");
            }
            const panelWindow = panel.windows[selection.index];
            const areaInSquareMeters = roundToDecimalSigns(
                panelWindow.area / SQUARE_FEET_IN_SQUARE_METER,
                DECIMAL_DIGITS_FOR_AREA,
            );
            setWindowArea(areaInSquareMeters);
            setOriginalWindowArea(areaInSquareMeters);
            setWindowWeight(panelWindow.weight);
            setOriginalWindowWeight(panelWindow.weight);
            setIsSaveInProgress(false);
        };

        onComponentSelectionChanged();

        eventBus.addEventListener("Dextall.Hooks.Designer.Panel.ComponentSelectionChanged", onComponentSelectionChanged);

        return () => {
            eventBus.removeEventListener("Dextall.Hooks.Designer.Panel.ComponentSelectionChanged", onComponentSelectionChanged);
        };
    }, [panel, selection]);
    const isDisabledSaving =
        isAlmostEqual(windowArea, originalWindowArea) && isAlmostEqual(windowWeight, originalWindowWeight);

    const updateWindowArea = (newArea: number | null) => {
        if (newArea !== null) {
            setWindowArea(newArea);
        }
    };

    const updateWindowWeight = (newWeight: number | null) => {
        if (newWeight !== null) {
            setWindowWeight(newWeight);
        }
    };

    const saveWindowDesignParameters = () => {
        if (!(selection && panel)) {
            return;
        }

        setIsSaveInProgress(true);

        eventBus.dispatchEvent({
            type: "Dextall.PanelTypes.UpdateWindowDesignParameters",
            payload: {
                panelTypeId: panel.panelTypeId,
                windowIndex: selection.index,
                area: windowArea * SQUARE_FEET_IN_SQUARE_METER,
                weight: windowWeight,
            },
        });
    };

    return (
        <>
            <ParametersLabel label="Design specifications:" />
            {/* 
                TODO: implement area editor control in metric/imperial units.
                square meters square decimal feet or another option?
                Does square fractional feet format exist?
            */}
            <NumberPropertyEditor
                title="Area, m²:"
                value={windowArea}
                onSave={updateWindowArea}
                auxiliaryControl={createWindowAreaParameterInfo()}
            />
            {/* 
                TODO: implement weight editor control in kg / lbs.
            */}
            <NumberPropertyEditor title="Weight, kg:" value={windowWeight} onSave={updateWindowWeight} />

            <div className="window-design-parameters-properties-buttons">
                <div className="buttons-container">
                    <Button title="Save" onClick={saveWindowDesignParameters} disabled={isDisabledSaving} />
                </div>
            </div>
            {isSaveInProgress &&
                ReactDOM.createPortal(
                    <ProcessingPane
                        isVisible={true}
                        message="Saving window design parameters..."
                        title="Window design parameters"
                    />,
                    rootElement,
                )}
        </>
    );
};

const SQUARE_FEET_IN_SQUARE_METER = 1 / (0.3048 * 0.3048);
const DECIMAL_DIGITS_FOR_AREA = 3;
